import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Clock, Plus, Pencil, Trash2, Save, X, Loader2, Users, Search, Upload, FileDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  name: '',
  day_checkin_time: '09:00',
  day_checkout_time: '20:00',
  tea1_window: '09:00-13:00',
  tea1_minutes: 15,
  tea2_window: '14:00-17:00',
  tea2_minutes: 15,
  tea_evening_window: '17:00-20:00',
  tea_evening_minutes: 15,
  lunch_window: '12:30-15:00',
  lunch_minutes: 60,
  permission_monthly_hours: 3,
};

export default function SchedulesMaster() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // schedule object or null
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [inUseOnly, setInUseOnly] = useState(false);
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(s => {
      if (inUseOnly && !(s.staff_count > 0)) return false;
      if (q && !(s.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, search, inUseOnly]);

  const fetchAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/schedules`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) setItems(await r.json());
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShowForm(true); setErr(''); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name,
      day_checkin_time: s.day_checkin_time || '09:00',
      day_checkout_time: s.day_checkout_time || '20:00',
      tea1_window: s.tea1_window || '09:00-13:00',
      tea1_minutes: s.tea1_minutes ?? 15,
      tea2_window: s.tea2_window || '14:00-17:00',
      tea2_minutes: s.tea2_minutes ?? 15,
      tea_evening_window: s.tea_evening_window || '17:00-20:00',
      tea_evening_minutes: s.tea_evening_minutes ?? 15,
      lunch_window: s.lunch_window || '12:30-15:00',
      lunch_minutes: s.lunch_minutes ?? 60,
      permission_monthly_hours: s.permission_monthly_hours ?? 3,
    });
    setShowForm(true);
    setErr('');
  };

  const save = async () => {
    if (!form.name.trim()) { setErr('Schedule name is required.'); return; }
    setBusy(true); setErr('');
    try {
      const token = localStorage.getItem('token');
      const body = {
        ...form,
        tea1_minutes: parseInt(form.tea1_minutes, 10) || 0,
        tea2_minutes: parseInt(form.tea2_minutes, 10) || 0,
        tea_evening_minutes: parseInt(form.tea_evening_minutes, 10) || 0,
        lunch_minutes: parseInt(form.lunch_minutes, 10) || 0,
        permission_monthly_hours: parseFloat(form.permission_monthly_hours) || 0,
      };
      const url = editing ? `${API_URL}/api/schedules/${editing.id}` : `${API_URL}/api/schedules`;
      const method = editing ? 'PUT' : 'POST';
      const r = await fetch(url, {
        method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || `Save failed (${r.status})`); return; }
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY);
      fetchAll();
    } finally { setBusy(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? ${s.staff_count > 0 ? `⚠️  ${s.staff_count} staff assigned` : ''}`)) return;
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/schedules/${s.id}`, {
      method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      alert(d.detail || 'Delete failed'); return;
    }
    fetchAll();
  };

  // ===== Bulk import (Excel/CSV) =====
  const downloadTemplate = () => {
    const headers = [
      'Name', 'Day Check-in', 'Day Check-out',
      'Morning Tea Window', 'Morning Tea Minutes',
      'Afternoon Tea Window', 'Afternoon Tea Minutes',
      'Evening Tea Window', 'Evening Tea Minutes',
      'Lunch Window', 'Lunch Minutes',
      'Permission Hours / Month',
    ];
    const example = [
      'Night Shift', '21:00', '06:00',
      '22:00-00:00', 15,
      '01:00-03:00', 15,
      '04:00-05:00', 15,
      '00:00-01:00', 45,
      3,
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, example]);
    ws['!cols'] = headers.map(h => ({ wch: Math.max(14, h.length + 2) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SCHEDULES');
    XLSX.writeFile(wb, 'schedules-template.xlsx');
  };

  const SCH_ALIASES = {
    name: ['name', 'schedule', 'schedule name'],
    day_checkin_time: ['day check-in', 'day checkin', 'check-in', 'checkin', 'day_checkin_time', 'in time'],
    day_checkout_time: ['day check-out', 'day checkout', 'check-out', 'checkout', 'day_checkout_time', 'out time'],
    tea1_window: ['morning tea window', 'tea 1 window', 'tea1 window', 'tea1_window'],
    tea1_minutes: ['morning tea minutes', 'tea 1 minutes', 'tea1 minutes', 'tea1_minutes'],
    tea2_window: ['afternoon tea window', 'tea 2 window', 'tea2 window', 'tea2_window'],
    tea2_minutes: ['afternoon tea minutes', 'tea 2 minutes', 'tea2 minutes', 'tea2_minutes'],
    tea_evening_window: ['evening tea window', 'tea evening window', 'tea_evening_window'],
    tea_evening_minutes: ['evening tea minutes', 'tea evening minutes', 'tea_evening_minutes'],
    lunch_window: ['lunch window', 'lunch_window'],
    lunch_minutes: ['lunch minutes', 'lunch_minutes', 'lunch (min)'],
    permission_monthly_hours: ['permission hours / month', 'permission hours', 'permission_monthly_hours', 'monthly permission'],
  };

  const pickCol = (obj, key) => {
    const aliases = SCH_ALIASES[key] || [key];
    for (const k of Object.keys(obj || {})) {
      const lower = String(k).trim().toLowerCase();
      if (aliases.includes(lower)) return obj[k];
    }
    return undefined;
  };

  const normaliseScheduleRows = (raw) => raw.map(r => {
    const v = (k) => {
      const x = pickCol(r, k);
      return (x === undefined || x === null || x === '') ? undefined : x;
    };
    const intOrUndef = (x) => (x === undefined ? undefined : (parseInt(x, 10) || 0));
    const floatOrUndef = (x) => (x === undefined ? undefined : (parseFloat(x) || 0));
    const row = {
      name: String(pickCol(r, 'name') ?? '').trim(),
      day_checkin_time: v('day_checkin_time') !== undefined ? String(v('day_checkin_time')).trim() : undefined,
      day_checkout_time: v('day_checkout_time') !== undefined ? String(v('day_checkout_time')).trim() : undefined,
      tea1_window: v('tea1_window') !== undefined ? String(v('tea1_window')).trim() : undefined,
      tea1_minutes: intOrUndef(v('tea1_minutes')),
      tea2_window: v('tea2_window') !== undefined ? String(v('tea2_window')).trim() : undefined,
      tea2_minutes: intOrUndef(v('tea2_minutes')),
      tea_evening_window: v('tea_evening_window') !== undefined ? String(v('tea_evening_window')).trim() : undefined,
      tea_evening_minutes: intOrUndef(v('tea_evening_minutes')),
      lunch_window: v('lunch_window') !== undefined ? String(v('lunch_window')).trim() : undefined,
      lunch_minutes: intOrUndef(v('lunch_minutes')),
      permission_monthly_hours: floatOrUndef(v('permission_monthly_hours')),
    };
    // Drop undefined keys so server-side merge fills from prior/defaults.
    Object.keys(row).forEach(k => row[k] === undefined && delete row[k]);
    return row;
  }).filter(r => r.name);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportMsg('');
    try {
      const ext = file.name.split('.').pop().toLowerCase();
      let raw;
      if (ext === 'csv') {
        const text = await file.text();
        const cleaned = text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text;
        const wb = XLSX.read(cleaned, { type: 'string' });
        raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
      } else {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
      }
      const rows = normaliseScheduleRows(raw);
      if (!rows.length) {
        setImportMsg('File empty or missing required Name column.');
        return;
      }
      if (!window.confirm(
        `Import ${rows.length} schedules?\n\n` +
        `• Existing schedules (matched by Name) will be UPDATED — assigned staff get the new times.\n` +
        `• Missing schedules will be CREATED.\n` +
        `• Blank fields fall back to existing values / standard defaults.`
      )) return;
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/schedules/bulk-import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setImportMsg(d.detail || `Import failed (${r.status})`); return; }
      setImportMsg(
        `Created ${d.created} · Updated ${d.updated} · ${d.skipped_invalid} skipped · ` +
        `${d.propagated_to_staff} staff records propagated.`
      );
      fetchAll();
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4" data-testid="master-schedules">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock size={18} className="text-orange-600" />
          <h2 className="font-semibold">Schedules</h2>
          <span className="text-xs text-gray-500" data-testid="schedule-count">
            {(search || inUseOnly) ? `${filtered.length}/${items.length}` : items.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={downloadTemplate}
            className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-100 rounded"
            data-testid="schedule-template-btn"
            title="Download Excel template"
          >
            <FileDown size={12} /> Template
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-1 px-2 py-1.5 text-xs text-emerald-700 hover:bg-emerald-50 rounded disabled:opacity-50"
            data-testid="schedule-import-btn"
            title="Import from Excel / CSV"
          >
            {importing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            Import
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            data-testid="schedule-import-file"
            onChange={handleFile}
          />
          <button
            onClick={openCreate}
            className="flex items-center gap-1 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm"
            data-testid="schedule-add-btn"
          >
            <Plus size={14} /> New Schedule
          </button>
        </div>
      </div>

      {importMsg && (
        <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1 mb-2" data-testid="schedule-import-msg">
          {importMsg}
        </p>
      )}

      {/* Search + filters — visible only when there are >3 schedules */}
      {items.length > 3 && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="schedule-filters">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search schedules by name…"
              className="w-full pl-8 pr-3 py-1.5 border rounded-lg text-xs"
              data-testid="schedule-search"
            />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={inUseOnly}
              onChange={(e) => setInUseOnly(e.target.checked)}
              data-testid="schedule-inuse-only"
            />
            In use only
          </label>
          {(search || inUseOnly) && (
            <button
              onClick={() => { setSearch(''); setInUseOnly(false); }}
              className="text-xs text-gray-500 hover:text-gray-800"
              data-testid="schedule-clear"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-xs text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-gray-500 text-center py-6">No schedules yet.</p>
      ) : filtered.length === 0 ? (
        <p className="text-xs text-gray-500 text-center py-6" data-testid="schedule-empty">No schedules match the current filter.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {filtered.map(s => (
            <li key={s.id} className="py-2 flex items-start justify-between gap-2" data-testid={`schedule-row-${s.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm">{s.name}</span>
                  <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">
                    <Users size={9} /> {s.staff_count || 0}
                  </span>
                </div>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {s.day_checkin_time}–{s.day_checkout_time} · lunch {s.lunch_minutes}m ({s.lunch_window})
                  · tea {s.tea1_minutes}+{s.tea2_minutes}+{s.tea_evening_minutes}m
                  · perm {s.permission_monthly_hours}h/mo
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => openEdit(s)}
                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                  data-testid={`schedule-edit-${s.id}`}
                  title="Edit"
                ><Pencil size={14} /></button>
                <button
                  onClick={() => del(s)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                  data-testid={`schedule-del-${s.id}`}
                  title="Delete"
                ><Trash2 size={14} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <ScheduleForm
          form={form}
          setForm={setForm}
          busy={busy}
          err={err}
          editing={editing}
          onSave={save}
          onCancel={() => { setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
}

function ScheduleForm({ form, setForm, busy, err, editing, onSave, onCancel }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-5" onClick={(e) => e.stopPropagation()} data-testid="schedule-form-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg">{editing ? 'Edit Schedule' : 'New Schedule'}</h3>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>

        <Field label="Schedule Name" required>
          <input
            type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g., Standard · Warehouse Shift · Night Shift"
            className="w-full px-3 py-2 border rounded-lg text-sm"
            data-testid="schedule-name-input"
            maxLength={80}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label="Day check-in"><TimeInput value={form.day_checkin_time} onChange={v => setForm({ ...form, day_checkin_time: v })} testId="sch-ci" /></Field>
          <Field label="Day check-out"><TimeInput value={form.day_checkout_time} onChange={v => setForm({ ...form, day_checkout_time: v })} testId="sch-co" /></Field>
        </div>

        <Section title="Tea breaks">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <WindowRow label="Morning Tea"  win={form.tea1_window}        minutes={form.tea1_minutes}
              onWin={v => setForm({ ...form, tea1_window: v })}         onMin={v => setForm({ ...form, tea1_minutes: v })}        testId="t1" />
            <WindowRow label="Afternoon Tea" win={form.tea2_window}        minutes={form.tea2_minutes}
              onWin={v => setForm({ ...form, tea2_window: v })}         onMin={v => setForm({ ...form, tea2_minutes: v })}        testId="t2" />
            <WindowRow label="Evening Tea"  win={form.tea_evening_window} minutes={form.tea_evening_minutes}
              onWin={v => setForm({ ...form, tea_evening_window: v })} onMin={v => setForm({ ...form, tea_evening_minutes: v })} testId="te" />
          </div>
        </Section>

        <Section title="Lunch">
          <div className="grid grid-cols-2 gap-3">
            <WindowRow label="Lunch" win={form.lunch_window} minutes={form.lunch_minutes}
              onWin={v => setForm({ ...form, lunch_window: v })} onMin={v => setForm({ ...form, lunch_minutes: v })} testId="l" />
            <Field label="Permission (hours / 26-day month)">
              <input type="number" min={0} step={0.5} value={form.permission_monthly_hours}
                onChange={(e) => setForm({ ...form, permission_monthly_hours: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                data-testid="sch-perm" />
            </Field>
          </div>
        </Section>

        {err && <p className="text-xs text-red-600 mt-2" data-testid="schedule-form-err">{err}</p>}

        <div className="flex items-center justify-end gap-2 mt-4">
          <button onClick={onCancel} disabled={busy} className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={onSave} disabled={busy}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-1 disabled:opacity-60"
            data-testid="schedule-save-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {editing ? 'Update & Propagate' : 'Create Schedule'}
          </button>
        </div>

        {editing && (
          <p className="text-[11px] text-gray-500 mt-3">
            Updates to this schedule will be copied to <strong>all staff currently assigned</strong>.
          </p>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-4">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">{title}</p>
      {children}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-gray-600 mb-1">{label}{required && <span className="text-red-500"> *</span>}</span>
      {children}
    </label>
  );
}

function TimeInput({ value, onChange, testId }) {
  return (
    <input
      type="time" value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border rounded-lg text-sm"
      data-testid={testId}
    />
  );
}

function WindowRow({ label, win, minutes, onWin, onMin, testId }) {
  return (
    <div>
      <p className="text-[11px] text-gray-600 mb-1">{label}</p>
      <input
        type="text" value={win} onChange={(e) => onWin(e.target.value)}
        placeholder="HH:MM-HH:MM"
        className="w-full px-2 py-1.5 border rounded-lg text-xs mb-1"
        data-testid={`${testId}-win`}
      />
      <input
        type="number" min={0} step={5} value={minutes}
        onChange={(e) => onMin(parseInt(e.target.value, 10) || 0)}
        placeholder="min"
        className="w-full px-2 py-1.5 border rounded-lg text-xs"
        data-testid={`${testId}-min`}
      />
    </div>
  );
}
